import React, { useEffect, useRef, useState } from 'react';
import { BarChart, Zap, ArrowRight, Smartphone, Code2, Palette, BrainCog } from 'lucide-react';
import { Link } from 'react-router-dom';

const Services = () => {
  const [visibleItems, setVisibleItems] = useState([]);
  const [hoveredItem, setHoveredItem] = useState(null);
  const sectionRef = useRef(null);
  const itemRefs = useRef([]);

  const services = [
    {
      icon: <BrainCog className="h-8 w-8 text-red-600" />,
      title: 'AI-Powered App Development',
      description: 'Build smarter apps that automate, predict, and personalize.',
      features: ['Chatbots & Voice Assistants', 'AI-generated reports, text, and images', 'Predictive analytics & personalization engines'],
      bgGradient: 'from-red-500/10 to-pink-500/10',
      iconBg: 'bg-red-500/20',
      hoverColor: 'hover:border-red-500/50'
    },
    {
      icon: <Code2 className="h-8 w-8 text-red-500" />,
      title: 'Custom Software Development',
      description: 'Scalable, secure, and business-specific solutions.',
      features: ['Enterprise-grade backend architecture', 'Domain-specific workflows and integrations', 'Cloud-native, API-first, and ready for scale'],
      bgGradient: 'from-red-500/10 to-orange-500/10',
      iconBg: 'bg-red-500/20',
      hoverColor: 'hover:border-red-500/50'
    },
    {
      icon: <Smartphone className="h-8 w-8 text-green-600" />,
      title: 'Mobile & Web App Development',
      description: 'Cross-platform digital experiences for startups and enterprises.',
      features: ['Cross-Platform Apps',  'App Store and Play Store Deployment','Smart UI/UX with AI-powered user behavior analysis'],
      bgGradient: 'from-green-500/10 to-emerald-500/10',
      iconBg: 'bg-green-500/20',
      hoverColor: 'hover:border-green-500/50'
    },
    {
      icon: <Palette className="h-8 w-8 text-indigo-500" />,
      title: 'Intelligent UI/UX Design',
      description: 'Crafted experiences that adapt to user behavior.',
      features: ['AI-driven A/B testing and personalization', 'Wireframes to working UI with GenAI', 'Clean, fast, and responsive design systems'],
      bgGradient: 'from-indigo-500/10 to-red-500/10',
      iconBg: 'bg-indigo-500/20',
      hoverColor: 'hover:border-indigo-500/50'
    },
    {
      icon: <BarChart className="h-8 w-8 text-yellow-500" />,
      title: 'Data Analytics',
      description: 'Unlock insights from your data to drive smarter business decisions.',
      features: ['Data Visualization', 'Predictive Analytics', 'Business Intelligence'],
      bgGradient: 'from-yellow-500/10 to-amber-500/10',
      iconBg: 'bg-yellow-500/20',
      hoverColor: 'hover:border-yellow-500/50'
    },
    {
      icon: <Zap className="h-8 w-8 text-purple-500" />,
      title: 'MVP Development',
      description: 'Launch your product quickly with a Minimum Viable Product to validate your idea.',
      features: ['Rapid Prototyping', 'Feature Prioritization', 'Market Validation'],
      bgGradient: 'from-purple-500/10 to-pink-500/10',
      iconBg: 'bg-purple-500/20',
      hoverColor: 'hover:border-purple-500/50'
    }
  ];

  useEffect(() => {
    const observers = itemRefs.current.map((ref, index) => {
      if (!ref) return null;
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setVisibleItems(prev => [...prev, index]);
          }
        },
        { threshold: 0.2 }
      );
      observer.observe(ref);
      return observer;
    });

    return () => {
      observers.forEach(observer => observer?.disconnect());
    };
  }, []);

  return (
    <section ref={sectionRef} className="py-16 sm:py-12 bg-white relative overflow-hidden">
      {/* Background pattern */}
      <div className="absolute inset-0 bg-gradient-to-br from-gray-50 via-white to-red-50/30"></div>
      
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10 sm:mb-8">
          <div className="flex items-center justify-center space-x-2 mb-4">
            <div className="w-8 sm:w-10 h-0.5 bg-corporate-accent"></div>
            <span className="text-corporate-title font-semibold text-base sm:text-lg tracking-wide">WHAT WE DO</span>
            <div className="w-8 sm:w-10 h-0.5 bg-corporate-accent"></div>
          </div>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-corporate-gray-dark mb-3 sm:mb-4">
            Delivering Custom Digital Products with Built-in <span className="text-corporate-red">Intelligence.</span>
          </h2>
          <p className="text-base sm:text-lg text-corporate-gray max-w-2xl sm:max-w-3xl mx-auto">
            We design, develop, and deploy full-stack digital products powered by the latest AI innovations—tailored to your business.
          </p>
        </div>

        <div className="grid grid-cols-1  sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-2 lg:gap-80">
          {services.map((service, index) => (
            <Link
              to="/services"
              key={service.title}
              ref={el => itemRefs.current[index] = el}
              onMouseEnter={() => setHoveredItem(index)}
              onMouseLeave={() => setHoveredItem(null)}
              className={`group relative bg-white p-4  sm:p-3 lg:p-6 rounded-xl sm:rounded-2xl shadow-md hover:shadow-xl transition-all duration-500 transform border border-gray-100 cursor-pointer overflow-hidden
                ${visibleItems.includes(index) 
                  ? 'opacity-100 translate-y-0 scale-100' 
                  : 'opacity-0 translate-y-8 scale-95'
                }
                hover:-translate-y-2 hover:scale-105 ${service.hoverColor}`}
              style={{ 
                transitionDelay: `${index * 120}ms`,
                minHeight: 'auto',
                maxWidth: '100%',
                textDecoration: 'none'
              }}
            >
              <div className={`absolute inset-0 bg-gradient-to-br ${service.bgGradient} opacity-0 group-hover:opacity-100 transition-opacity duration-300`}></div>
              
              <div className="relative z-10 flex flex-col h-full justify-between">
                <div>
                  <div className={`${service.iconBg} w-12 h-12 sm:w-14 sm:h-14 rounded-xl flex items-center justify-center mb-2 sm:mb-6 transition-all duration-300 group-hover:scale-110 group-hover:rotate-3 group-hover:${service.iconBg.replace('bg-', 'bg-opacity-40 ')} group-hover:shadow-lg`}> 
                    {React.cloneElement(service.icon, {
                      className: `${service.icon.props.className} transition-all duration-300 group-hover:scale-125 group-hover:drop-shadow-lg`
                    })}
                  </div>
                  <h3 className="text-lg sm:text-xl font-semibold text-corporate-gray-dark mb-2 sm:mb-3 group-hover:text-corporate-red transition-colors duration-300">
                    {service.title}
                  </h3>
                  <p className="text-corporate-gray-dark font-medium mb-3 sm:mb-4 leading-relaxed text-sm sm:text-base">
                    {service.description}
                  </p>
                  <div>
                  {/* <ul className="pl-0 space-y-2 sm:space-y-3 mb-1 sm:mb-2 text-left list-outside">
                    {service.features.map((feature, featureIndex) => (
                      <li 
                        key={feature} 
                        className={`text-xs sm:text-sm text-corporate-gray flex items-center transition-all duration-300 ${
                          visibleItems.includes(index) ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4'
                        }`}
                        style={{ transitionDelay: `${(index * 120) + (featureIndex * 80)}ms` }}
                      >
                        <svg
                          className="w-4 h-4 sm:mr-3 flex-shrink-0"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                          style={{ minWidth: '1rem' }}
                        >
                          <defs>
                            <linearGradient id="check-gradient" x1="0" y1="0" x2="24" y2="24" gradientUnits="userSpaceOnUse">
                              <stop stopColor="#ca262c" />
                              <stop offset="1" stopColor="#a01f24" />
                            </linearGradient>
                          </defs>
                          <circle cx="12" cy="12" r="10" stroke="url(#check-gradient)" strokeWidth="2" fill="none" />
                          <path d="M8 12.5l2.2 2L16 9.5" stroke="url(#check-gradient)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                        </svg>
                        {feature}
                      </li>
                    ))}
                  </ul> */}
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Services;